/**
 * Shared helpers and hooks for Company Portal event create/edit forms.
 * Extracted to avoid duplication between CreateEvent and EditEvent containers.
 */

import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import * as companyPortalEventsApi from "../../../network/apis/companyPortalEvents";
import { lookupsKeys } from "../../../utils/Constants";

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

export const getLookupList = (res, lookupKey) => {
  const root = res?.data?.data;

  if (Array.isArray(root)) {
    const lookupBucket = root.find((bucket) =>
      Array.isArray(bucket?.[lookupKey]),
    );
    return lookupBucket?.[lookupKey] ?? [];
  }

  return Array.isArray(root?.[lookupKey]) ? root[lookupKey] : [];
};

/** Same shape as admin `state.lookups.general.roles` for `UISelect` / role labels. */
export const mapLookupRowsToOptions = (rows) =>
  (rows || []).map((row) => ({
    id: row.id,
    name: String(row.name ?? row.label ?? "").trim() || String(row.id ?? ""),
    // Locale-independent name, kept alongside the localized `name` so callers can
    // reliably identify fixed system roles (e.g. "Supervisor") regardless of UI language.
    name_en: row.name_en,
  }));

export const mergeEventSnapshot = (current, unwrapped) => {
  const attrs = unwrapped?.attributes;
  if (!attrs || typeof attrs !== "object") return current;

  return {
    ...(current || {}),
    ...attrs,
    id: attrs.id ?? unwrapped?.id ?? current?.id,
  };
};

// ---------------------------------------------------------------------------
// Shared hook — loads requirements + roles lookup options once on mount
// ---------------------------------------------------------------------------

export const useCompanyPortalLookups = () => {
  const lang = useSelector((state) => state.locale.lang);
  const [requirementsOptions, setRequirementsOptions] = useState(
    /** @type {any[]} */
    ([]),
  );
  const [rolesOptions, setRolesOptions] = useState(
    /** @type {{ id: unknown; name: string }[]} */
    ([]),
  );
  const [eventTypeOptions, setEventTypeOptions] = useState(
    /** @type {any[]} */
    ([]),
  );

  // `lang` is intentionally a dep here: the backend returns requirement/role
  // names localized to the request's `locale` header, so this catalog is
  // stale after a language switch unless re-fetched.
  useEffect(() => {
    let cancelled = false;

    companyPortalEventsApi
      .getCompanyPortalLookups([
        lookupsKeys.requirements,
        lookupsKeys.roles,
        lookupsKeys.eventTypes,
      ])
      .then((res) => {
        if (cancelled) return;
        const requirementsList = getLookupList(res, lookupsKeys.requirements);
        const rolesList = getLookupList(res, lookupsKeys.roles);
        const eventTypesList = getLookupList(res, lookupsKeys.eventTypes);
        setRequirementsOptions(requirementsList);
        setRolesOptions(mapLookupRowsToOptions(rolesList));
        setEventTypeOptions(eventTypesList);
      })
      .catch(() => {
        if (!cancelled) {
          setRequirementsOptions([]);
          setRolesOptions([]);
          setEventTypeOptions([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [lang]);

  return { requirementsOptions, rolesOptions, eventTypeOptions };
};

// ---------------------------------------------------------------------------
// Shared hook — tracks event start/end dates for shift time resolution
// ---------------------------------------------------------------------------

export const useScheduleBounds = () => {
  const [scheduleBounds, setScheduleBounds] = useState(null);

  const updateScheduleBounds = useCallback((attrs = {}, body = {}) => {
    const start =
      attrs.event_start_at ?? body.event_start_at ?? attrs.eventStartAt;
    const end = attrs.event_end_at ?? body.event_end_at ?? attrs.eventEndAt;
    if (!start || !end) return;
    setScheduleBounds({ eventStartAt: start, eventEndAt: end });
  }, []);

  return { scheduleBounds, updateScheduleBounds };
};
